/*
 * Build the Plan Builder submission envelope and POST it via the
 * shared API client. The payload shape MUST match
 * api/MotrTools.Api/Validation/PlanBuilderV1Validator.cs and the
 * field names projected by vw_PlanBuilderSubmissions.
 *
 * The envelope itself (tool_id, schema_version, client_submission_id,
 * payload) is a SubmissionRequest — the inner `payload` is the
 * tool-specific shape.
 */
import { api } from '@shared/api/client';
import { getClientId } from '@shared/clientId';
import type { SharedLens } from '@shared/lens';
import type { SubmissionRequest } from '@shared/types/api';
import type { PlanBuilderConfig } from './config';
import { buildPlanMarkdown } from './plan-markdown';
import { extractMiracleThemes, matchActions } from './plan-generation';
import type {
  ContactInfo,
  DiagnosticAnswers,
  OrgProfile,
  PlanBuilderSubmissionPayload,
  ScoreResult,
} from './types';

const SUBMISSION_ID_KEY = 'motr_plan_submission_id';

/**
 * Construct the submission payload from the live lens, answers, and
 * computed score. Validation: all required fields must be set; if
 * not, returns null (the caller should not have called us).
 */
export function buildSubmissionPayload(
  lens: SharedLens,
  answers: DiagnosticAnswers,
  score: ScoreResult,
): PlanBuilderSubmissionPayload | null {
  if (
    !lens.jobStatement ||
    !lens.frictionPhase ||
    !lens.flexibility ||
    answers.op1 === undefined ||
    answers.op2 === undefined ||
    answers.op3 === undefined ||
    answers.op4 === undefined ||
    answers.op5 === undefined ||
    answers.exp1 === undefined ||
    answers.exp2 === undefined ||
    answers.exp3 === undefined ||
    answers.exp4 === undefined ||
    answers.exp5 === undefined ||
    answers.exp6 === undefined ||
    answers.gap1 === undefined ||
    !answers.gap2 ||
    !answers.gap3
  ) {
    return null;
  }
  const themes = extractMiracleThemes(answers);
  return {
    lens: {
      jobStatement: lens.jobStatement,
      frictionPhase: lens.frictionPhase,
      outcomes: [
        lens.outcomes[0] ?? '',
        lens.outcomes[1] ?? '',
        lens.outcomes[2] ?? '',
      ],
      flexibility: lens.flexibility,
      emotional: {
        feel: lens.emotional.feel,
        avoid: lens.emotional.avoid,
      },
      redFlagWordsHit: lens.redFlagWordsHit,
    },
    diagnostic: {
      op1: answers.op1,
      op2: answers.op2,
      op3: answers.op3,
      op4: answers.op4,
      op5: answers.op5,
      exp1: answers.exp1,
      exp2: answers.exp2,
      exp3: answers.exp3,
      exp4: answers.exp4,
      exp5: answers.exp5,
      exp6: answers.exp6,
      miracle_desk: answers.miracle_desk,
      miracle_midday: answers.miracle_midday,
      miracle_gc: answers.miracle_gc,
      gap1: answers.gap1,
      gap2: answers.gap2,
      gap3: answers.gap3,
    },
    computed: {
      stage: score.stage,
      stage_name: score.stageData.name,
      weakest_drivers: score.weakest,
      strongest_driver: score.strongest,
      joy_score: score.joyScore,
      alignment_gap: score.alignmentGap,
      miracle_filled: themes.filled,
      miracle_themes: {
        levers: Array.from(themes.levers),
        drivers: Array.from(themes.drivers),
      },
    },
  };
}

/**
 * POST the submission. Idempotency is preserved by reusing a stable
 * `client_submission_id` per browser — the server upserts on
 * (tool_id, client_submission_id). Errors are swallowed so the
 * results page still renders if the network is down (the request
 * gets queued by the API client for the next page load).
 */
export async function submitPlan(
  lens: SharedLens,
  answers: DiagnosticAnswers,
  score: ScoreResult,
  completionSeconds?: number,
): Promise<string | null> {
  const payload = buildSubmissionPayload(lens, answers, score);
  if (!payload) return null;
  if (completionSeconds !== undefined) {
    payload.computed = { ...payload.computed, completion_seconds: completionSeconds };
  }
  const clientId = getClientId();
  const clientSubmissionId = getOrCreateSubmissionId();
  const req: SubmissionRequest<PlanBuilderSubmissionPayload> = {
    tool_id: 'plan_builder',
    schema_version: 'v1',
    client_submission_id: clientSubmissionId,
    submitted_at: new Date().toISOString(),
    user_agent: navigator.userAgent,
    referrer: document.referrer || undefined,
    payload,
  };
  try {
    const res = await api.submit(clientId, req);
    return res.submission_id;
  } catch {
    // The API client already queued the request for next load. The
    // results page should still render even if the POST failed.
    return null;
  }
}

/** Exposed so CaptureCard can reuse the same id when re-POSTing. */
export function currentSubmissionId(): string {
  return getOrCreateSubmissionId();
}

function getOrCreateSubmissionId(): string {
  try {
    const existing = localStorage.getItem(SUBMISSION_ID_KEY);
    if (existing) return existing;
  } catch {
    /* fall through */
  }
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : 'sub-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  try {
    localStorage.setItem(SUBMISSION_ID_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

/**
 * Re-POST the submission with contact + optional org_profile attached.
 * Reuses the same client_submission_id as the original submitPlan() call
 * so the server upserts and bumps Version rather than creating a new row.
 */
export async function submitPlanContact(
  lens: SharedLens,
  answers: DiagnosticAnswers,
  score: ScoreResult,
  contact: ContactInfo,
  config: PlanBuilderConfig,
  orgProfile?: OrgProfile,
): Promise<string | null> {
  const payload = buildSubmissionPayload(lens, answers, score);
  if (!payload) return null;
  payload.contact = contact;
  if (orgProfile) payload.org_profile = orgProfile;
  // Attach the plan markdown so the backend can email it via Gmail SMTP.
  payload.report_markdown = buildPlanMarkdown(
    score,
    answers,
    matchActions(score, answers, config, { limit: 4 }),
    config,
  );

  const clientId = getClientId();
  const clientSubmissionId = currentSubmissionId();
  const req: SubmissionRequest<PlanBuilderSubmissionPayload> = {
    tool_id: 'plan_builder',
    schema_version: 'v1',
    client_submission_id: clientSubmissionId,
    submitted_at: new Date().toISOString(),
    user_agent: navigator.userAgent,
    referrer: document.referrer || undefined,
    payload,
  };
  const res = await api.submit(clientId, req);
  return res.submission_id;
}

export function clearSubmissionId(): void {
  try {
    localStorage.removeItem(SUBMISSION_ID_KEY);
  } catch {
    /* ignore */
  }
}
